/**
 * RegimeHeatmap
 *
 * At-a-glance view of regime states across the entire watchlist. Renders
 * a grid where rows are symbols and columns are the three independent
 * regime models (trend / momentum / cycle). Each cell is coloured by the
 * state's directional bias using the same palette the Briefing badges
 * use, so the two panels read consistently.
 *
 * Reads briefing data from Redux (lifted from BriefingPanel local state)
 * so this component doesn't trigger a duplicate /briefing/daily fetch.
 */
/* eslint-disable eqeqeq */
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import { fetchBriefing } from '../marketsSlice';
import { getStatePalette, isFresh } from '../panels/BriefingPanel';

// One regime cell. Pure visual: tinted background using the same RGB
// triplet the badges use, with the state name in small mono and a "·"
// dot if the transition is fresh.
//
// When `state` is missing the backend hasn't computed regime data for
// this symbol yet. Show a "pending" placeholder rather than a blank grey
// box so the empty state reads as in-progress rather than broken.
const HeatmapCell = ({ state, since }) => {
  if (!state) {
    return (
      <Tooltip title="Regime data is computed nightly. This symbol isn't covered yet — check back soon." arrow>
        <Box
          sx={{
            height: 28,
            px: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--dwlf-ink-02)',
            border: '1px dashed var(--dwlf-ink-08)',
            color: 'var(--dwlf-ink-25)',
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          pending
        </Box>
      </Tooltip>
    );
  }
  const { rgb } = getStatePalette(state);
  const isNeutral = rgb === '255, 255, 255';
  const fresh = isFresh(since);
  const bgAlpha = isNeutral ? 0.05 : 0.16;
  const borderAlpha = fresh ? (isNeutral ? 0.4 : 0.55) : (isNeutral ? 0.12 : 0.22);
  return (
    <Tooltip title={`${state}${since ? ` · since ${since.slice(0, 10)}` : ''}`} arrow>
      <Box
        sx={{
          height: 28,
          px: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          backgroundColor: `rgba(${rgb}, ${bgAlpha})`,
          border: `1px solid rgba(${rgb}, ${borderAlpha})`,
          color: `rgba(${rgb}, ${isNeutral ? 0.55 : 0.95})`,
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.65rem',
          letterSpacing: '0.02em',
          textTransform: 'lowercase',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {state}
        </Box>
        {fresh && (
          <Box
            component="span"
            sx={{
              fontSize: '0.5rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              opacity: 0.85,
              flexShrink: 0,
            }}
          >
            new
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

const ColumnHeader = ({ children }) => (
  <Typography
    sx={{
      fontFamily: '"IBM Plex Mono", monospace',
      fontSize: '0.6rem',
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      color: 'var(--dwlf-ink-40)',
      px: 1,
    }}
  >
    {children}
  </Typography>
);

const RegimeHeatmap = () => {
  const dispatch = useDispatch();
  const briefingData = useSelector((s) => s.markets.briefingData);
  const briefingLoading = useSelector((s) => s.markets.briefingLoading);
  const briefingError = useSelector((s) => s.markets.briefingError);
  const briefingLastFetched = useSelector((s) => s.markets.briefingLastFetched);

  // Safe to dispatch on mount — the slice's condition guard skips when a
  // fetch is already in flight or the cached data is fresh, so we don't
  // race the BriefingPanel's own dispatch.
  useEffect(() => {
    dispatch(fetchBriefing());
  }, [dispatch]);

  const rows = useMemo(() => {
    if (!briefingData?.symbols) {return [];}
    return Object.entries(briefingData.symbols).map(([symbol, data]) => ({
      symbol,
      trend: data?.regime?.trend,
      momentum: data?.regime?.momentum,
      cycle: data?.regime?.cycle,
    }));
  }, [briefingData]);

  // Only surface the coverage footnote when at least one cell is
  // actually pending — otherwise the note confuses users by promising
  // future population of symbols that are already fully covered.
  const hasPendingCells = useMemo(
    () => rows.some((row) => !row.trend?.state || !row.momentum?.state || !row.cycle?.state),
    [rows],
  );

  const resolved = briefingLastFetched != null || briefingError != null;
  const isLoading = briefingLoading || !resolved;

  if (isLoading) {
    return (
      <Box>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={32}
            sx={{ mb: 0.75, bgcolor: 'var(--dwlf-ink-03)' }}
          />
        ))}
      </Box>
    );
  }

  if (briefingError) {
    return (
      <Typography
        sx={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.8rem',
          color: 'rgba(248, 81, 73, 0.8)',
        }}
      >
        {briefingError}
      </Typography>
    );
  }

  if (rows.length === 0) {
    return (
      <Typography
        sx={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.8rem',
          color: 'var(--dwlf-ink-45)',
          lineHeight: 1.7,
        }}
      >
        No regime data yet. Add some symbols to your watchlist to see the
        heatmap populate.
      </Typography>
    );
  }

  return (
    <Box>
      {/* Column headers — align with the row template below. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '70px 1fr 1fr 1fr',
          gap: 0.75,
          mb: 1,
          px: 0.5,
        }}
      >
        <Box />
        <ColumnHeader>Trend</ColumnHeader>
        <ColumnHeader>Momentum</ColumnHeader>
        <ColumnHeader>Cycle</ColumnHeader>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {rows.map((row) => (
          <Box
            key={row.symbol}
            sx={{
              display: 'grid',
              gridTemplateColumns: '70px 1fr 1fr 1fr',
              gap: 0.75,
              alignItems: 'center',
              px: 0.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'var(--dwlf-text)',
                letterSpacing: '0.02em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.symbol}
            </Typography>
            <HeatmapCell state={row.trend?.state} since={row.trend?.since} />
            <HeatmapCell state={row.momentum?.state} since={row.momentum?.since} />
            <HeatmapCell state={row.cycle?.state} since={row.cycle?.since} />
          </Box>
        ))}
      </Box>

      {/* Coverage footnote — only surfaced when there are actually
          pending cells on screen, so users with full coverage don't see
          a confusing note about symbols that don't exist. */}
      {hasPendingCells && (
        <Typography
          sx={{
            mt: 2,
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.65rem',
            color: 'var(--dwlf-ink-35)',
            lineHeight: 1.6,
          }}
        >
          Regime data is computed nightly. Coverage is expanding — any
          &ldquo;pending&rdquo; symbols should populate soon.
        </Typography>
      )}
    </Box>
  );
};

export default RegimeHeatmap;
